import { Link } from 'react-router-dom';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import QuickEditImage from '@/components/quickedit/QuickEditImage';

interface ImageGalleryProps {
  section: any;
}

export default function ImageGallery({ section }: ImageGalleryProps) {
  const images: any[] = section.content?.images ?? [];
  const headingId = `gallery-heading-${section.id}`;
  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  if (images.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-[--bg-primary]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {section.title && (
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-sage-900 text-center mb-10 sm:text-4xl"
          >
            {isActive ? (
              <QuickEditInline
                value={section.title}
                onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                label="Image gallery title"
                as="span"
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              />
            ) : section.title}
          </h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img: any, i: number) => {
            const content = (
              <div className="relative overflow-hidden rounded-lg aspect-square">
                <QuickEditImage
                  src={img.url}
                  onSave={(url) => {
                    const newImages = [...(section.content?.images || [])];
                    newImages[i] = { ...newImages[i], url };
                    return mutation.mutateAsync({ content: { ...section.content, images: newImages } });
                  }}
                  label={`Gallery image ${i + 1}`}
                  targetWidth={1200}
                  targetHeight={900}
                >
                  <img
                    src={img.url}
                    alt={img.alt || ''}
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </QuickEditImage>
                {img.caption && (
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 text-lg font-semibold text-white">
                    {img.caption}
                  </span>
                )}
              </div>
            );

            if (img.link) {
              return (
                <Link
                  key={i}
                  to={img.link}
                  className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded-lg"
                >
                  {content}
                </Link>
              );
            }

            return <div key={i}>{content}</div>;
          })}
        </div>
      </div>
    </section>
  );
}
